// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// One guide page per machine, written from the machine's own definition (S3-10).
//
// THE MEASUREMENT THIS EXISTS FOR
// -------------------------------
// The Search Console export of 2026-10-07: three pages indexed out of nineteen known, fourteen
// "Discovered – currently not indexed", and the five `/play/{machine}` pages carrying **43 to 45
// words** each before JavaScript — the machine's name, its tagline, the navigation and the
// `<noscript>` sentence, identical but for two strings. Five near-duplicates of that size are the
// pages a search engine finds and declines.
//
// WHY A NEW PAGE AND NOT WORDS ON THE MACHINE PAGE
// ------------------------------------------------
// S3-09 already refused that, and the reasons still hold: the machine page is the cabinet, full
// bleed, and `.stage` is `overflow: hidden; touch-action: none` so a phone cannot scroll while a
// ball is in play. Words below it are words a phone may not reach, and words in the prerender that
// the page never shows are cloaking (`prerender.ts`, "WHAT IT MUST NEVER DO"). So the words get
// their own address — `/machines/{id}` — that is a document in the way `/how-to-play` is, read by
// `CopyPage`'s own markup, and whose one primary action is the machine page.
//
// WHY THE FACTS AND NOT THE BUILDER'S RULESHEET
// ---------------------------------------------
// `src/builder/rulesheet.ts` already phrases every rule in English, and it was measured before it
// was rejected here: **3,292 words for NEON NOVA**, written for the machine's *author* — "play lane
// chime", "change the flasher star left lamp", "ASTEROID RUN has no objective shots yet". That last
// one is a Builder diagnostic, and on a public page it reads as a statement that the mode is broken.
// What a player wants to know is smaller and every part of it is in the definition: what kind of
// machine, for how many, how long a game is, what the cabinet's own instruction card says, which
// modes there are and what they ask for, and how many balls multiball puts in play.
//
// WHAT IT MUST NEVER DO
// ---------------------
// **Say something the definition does not.** No adjective is chosen here — the difficulty is
// stated as the author's number out of five, the ball time as the author's estimate, an award only
// when it is a literal number. A field this file cannot phrase is a throw, never a guess, for the
// reason `vite.config.ts` gives about `meta.title`: a default is exactly what stops anybody
// noticing.
//
// **Imports carry `.ts`**, because `vite.config.ts` reaches this module through Node's own
// resolution (see `canonical.ts`), and **nothing here reads a disk or a clock**: the build reads
// the files and hands this the parsed JSON, and the suite does the same.

import { SITE_NAME, type CopySection, type PageCopy } from './pageCopy.ts'

/** The route pattern the guides are served at. `INDEXING` and `routes.ts` both name it. */
export const GUIDE_ROUTE = '/machines/:machineId'

/** The address of one machine's guide. */
export const guidePath = (id: string): string => `/machines/${id}`

/** One entry of the list the arcade links from: an address and the words on the link. */
export interface MachineGuide {
  readonly id: string
  readonly title: string
}

/**
 * A guide as the shell's `virtual:machine-guides` carries it: enough to name both of a machine's
 * pages before either page's own code has arrived.
 *
 * **Why the shell needs it.** On a cold load the prerendered `<title>` is already right, and
 * `AppShell` used to overwrite it with the route's generic copy — "Machine guide", "Play a
 * machine" — until the lazy page mounted and named itself. `pageTitle` and `description` are the
 * guide's own; `subtitle` is what `machineCopy` needs to rebuild exactly what `PlayRoute` sets.
 */
export interface ShellGuide extends MachineGuide {
  readonly pageTitle: string
  readonly description: string
  readonly subtitle?: string
}

/**
 * The words on a link to a guide.
 *
 * One function because two writers draw the link — the arcade in React and the arcade's prerendered
 * roster — and a link whose anchor text differs between them is the drift `prerender.ts` exists to
 * prevent.
 */
export const guideLinkText = (title: string): string => `${title} — rules and modes`

/**
 * The heading over the arcade's list of guides, and the name of the navigation landmark it heads.
 *
 * English, like everything else `pageCopy.ts` holds, because the prerenderer writes it and the
 * prerendered pages are English (report 01 §C4).
 */
export const GUIDES_HEADING = 'Machine guides'

/**
 * Each era, as the noun phrase a sentence needs.
 *
 * The four `validate.ts` allows. A fifth would arrive as a string this file has never read, and a
 * sentence built round an unknown token is the guess this module refuses — so it throws.
 */
const ERA_WORDS: Readonly<Record<string, string>> = {
  'em-1970s': '1970s electromechanical game',
  'ss-1980s': '1980s solid-state game',
  'dmd-1990s': '1990s dot-matrix game',
  'lcd-modern': 'modern LCD game',
}

/** `meta.contentRating`, in words. The same two tokens `prerender.ts` maps. */
const RATING_WORDS: Readonly<Record<string, string>> = { everyone: 'everyone', teen: 'teens and up' }

/**
 * What a player calls each kind of device, singular and plural — or `null` for a kind that is
 * plumbing rather than something a player aims at.
 *
 * **Every member of `DEVICE_KINDS` has a row**, and `test/seo/machineAbout.test.ts` walks the enum,
 * so a kind added to the format fails by name rather than vanishing from every playfield list.
 * `dropTargetBank` is `null` because its targets are counted already; the bank count is said
 * beside them.
 */
export const DEVICE_WORDS: Readonly<Record<string, readonly [string, string] | null>> = {
  flipper: ['flipper', 'flippers'],
  popBumper: ['pop bumper', 'pop bumpers'],
  slingshot: ['slingshot', 'slingshots'],
  standupTarget: ['standup target', 'standup targets'],
  dropTarget: ['drop target', 'drop targets'],
  dropTargetBank: null,
  spinner: ['spinner', 'spinners'],
  kicker: ['kicker', 'kickers'],
  magnet: ['magnet', 'magnets'],
  gate: null,
  rollover: ['rollover', 'rollovers'],
  captiveBall: ['captive ball', 'captive balls'],
  plunger: null,
  autoPlunger: null,
  ballSaveGate: null,
  diverter: null,
}

const NUMBER_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']

/** A count as a word up to ten, as digits after it. */
function count(n: number): string {
  return NUMBER_WORDS[n] ?? String(n)
}

/** `2000000` as `2,000,000`, the way the display draws a score. No locale is read. */
function points(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/gu, ',')
}

/** `a, b and c`. */
function list(items: readonly string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/**
 * An id as words: `wormholeRamp` → `wormhole ramp`.
 *
 * The same mechanical transformation `src/builder/rulesheet.ts`'s `humanise` makes, and
 * deliberately not imported from it — that module resolves its own imports without extensions,
 * which the build's config loader cannot follow. The suite holds the two to the same answer for
 * every id the shipped machines use, so they cannot drift apart quietly.
 */
export function humaniseId(id: string): string {
  return id
    .replace(/([a-z0-9])([A-Z])/gu, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/gu, '$1 $2')
    .toLowerCase()
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** Every object in a tree, depth first. The rules are a tree of action lists and expressions. */
function* objectsIn(value: unknown): Generator<Record<string, unknown>> {
  if (Array.isArray(value)) {
    for (const item of value) yield* objectsIn(item)
  } else if (isRecord(value)) {
    yield value
    for (const item of Object.values(value)) yield* objectsIn(item)
  }
}

/** Everything this module needs to know about one machine, before any of it is phrased. */
interface Facts {
  readonly title: string
  readonly subtitle: string | undefined
  readonly author: string
  readonly era: string
  readonly difficulty: number
  readonly playersMax: number
  readonly ballsPerGame: number
  readonly ballSeconds: number | undefined
  readonly rating: string
  readonly devices: ReadonlyMap<string, number>
  readonly modes: readonly ModeFacts[]
  readonly mostBalls: number | undefined
  readonly extraBalls: boolean
  readonly card: readonly string[]
  readonly rulesNotes: readonly string[]
}

interface ModeFacts {
  readonly objective: string | undefined
  readonly title: string
  readonly shots: readonly string[]
  readonly seconds: number | undefined
  readonly perShot: number | undefined
  readonly onComplete: number | undefined
}

/** The literal score an action list awards first, if it awards one. An expression is not phrased. */
function literalAward(actions: unknown): number | undefined {
  if (!Array.isArray(actions)) return undefined
  for (const action of actions) {
    if (isRecord(action) && typeof action['award'] === 'number') return action['award']
  }
  return undefined
}

/**
 * Read the facts out of a parsed definition and its presentation, refusing what cannot be phrased.
 *
 * `core/ts/machine/validate.ts` has already required most of these on every machine that loads, so
 * a throw here is a definition the product itself would refuse — this is not a second validator,
 * it is the reader declining to invent.
 */
function factsOf(id: string, definition: unknown, presentation: unknown): Facts {
  const fail = (what: string): never => {
    throw new Error(`machine guide: machines/${id} ${what}`)
  }
  if (!isRecord(definition)) return fail('is not a definition')
  const meta = definition['meta']
  if (!isRecord(meta)) return fail('has no meta')

  const text = (value: unknown, field: string): string => {
    if (typeof value !== 'string' || value.trim() === '') return fail(`has no usable ${field}`)
    return value.trim()
  }
  const whole = (value: unknown, field: string, min: number, max: number): number => {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < min || value > max) {
      return fail(`has no usable ${field}`)
    }
    return value
  }

  const era = ERA_WORDS[text(meta['era'], 'meta.era')] ?? fail(`has an era this guide cannot name: ${String(meta['era'])}`)
  const rating =
    RATING_WORDS[text(meta['contentRating'], 'meta.contentRating')] ??
    fail(`has a rating this guide cannot name: ${String(meta['contentRating'])}`)
  const author = isRecord(meta['author']) ? text(meta['author']['displayName'], 'meta.author.displayName') : fail('has no meta.author')
  const subtitle = typeof meta['subtitle'] === 'string' && meta['subtitle'].trim() !== '' ? meta['subtitle'].trim() : undefined
  const ballSeconds =
    meta['estimatedBallTimeSeconds'] === undefined
      ? undefined
      : whole(meta['estimatedBallTimeSeconds'], 'meta.estimatedBallTimeSeconds', 1, 3600)

  const devices = new Map<string, number>()
  const deviceList = definition['devices']
  if (!Array.isArray(deviceList)) return fail('has no devices')
  for (const device of deviceList) {
    const kind = isRecord(device) ? device['kind'] : undefined
    if (typeof kind !== 'string' || !(kind in DEVICE_WORDS)) {
      return fail(`has a device kind this guide cannot name: ${String(kind)}`)
    }
    devices.set(kind, (devices.get(kind) ?? 0) + 1)
  }

  const rules = definition['rules']
  if (!isRecord(rules)) return fail('has no rules')
  const timers = new Map<string, number>()
  for (const timer of Array.isArray(rules['timers']) ? rules['timers'] : []) {
    if (isRecord(timer) && typeof timer['id'] === 'string' && typeof timer['durationMs'] === 'number') {
      timers.set(timer['id'], timer['durationMs'])
    }
  }

  const modes: ModeFacts[] = []
  const goals = isRecord(presentation) && isRecord(presentation['modeGoals']) ? presentation['modeGoals'] : undefined
  const declaredModes = Array.isArray(rules['modes']) ? rules['modes'] : []
  for (const id of Object.keys(goals ?? {})) {
    if (!declaredModes.some(mode => isRecord(mode) && mode['id'] === id)) return fail(`has an objective for an undeclared mode: ${id}`)
  }
  for (const mode of Array.isArray(rules['modes']) ? rules['modes'] : []) {
    if (!isRecord(mode)) return fail('has a mode that is not an object')
    const timer = typeof mode['timer'] === 'string' ? mode['timer'] : undefined
    const ms = timer === undefined ? undefined : timers.get(timer)
    if (timer !== undefined && ms === undefined) return fail(`names a timer it does not declare: ${timer}`)
    modes.push({
      objective: goals?.[String(mode['id'])] === undefined ? undefined : text(goals[String(mode['id'])], 'mode objective'),
      title: text(mode['title'], 'mode title'),
      shots: (Array.isArray(mode['shots']) ? mode['shots'] : []).map((shot) => humaniseId(text(shot, 'mode shot'))),
      // Whole seconds, because every clock these machines declare is one and a guide that said
      // "34.999 seconds" would be describing arithmetic rather than a machine.
      seconds: ms === undefined ? undefined : Math.round(ms / 1000),
      perShot: literalAward(mode['onShot']),
      onComplete: literalAward(mode['onComplete']),
    })
  }

  // `startMultiball.balls` is how many balls are **in play**, not how many are added — `Game.ts`
  // serves `balls - ballsInPlay()` — and it defaults to 3, which is `RuleEngine`'s own default.
  let mostBalls: number | undefined
  let extraBalls = false
  for (const node of objectsIn(rules)) {
    if ('startMultiball' in node) {
      const spec = node['startMultiball']
      const balls = isRecord(spec) && typeof spec['balls'] === 'number' ? spec['balls'] : 3
      mostBalls = Math.max(mostBalls ?? 0, balls)
    }
    if (node['awardExtraBall'] === true) extraBalls = true
  }

  // The cabinet's instruction card, as its author wrote it — the one piece of prose here that was
  // written for a player, and the reason this page quotes it rather than paraphrasing it.
  const card: string[] = []
  const rulesNotes: string[] = []
  if (isRecord(presentation) && presentation['rulesNotes'] !== undefined) {
    if (!Array.isArray(presentation['rulesNotes'])) return fail('has rules notes that are not a list')
    for (const note of presentation['rulesNotes']) rulesNotes.push(text(note, 'rules note'))
  }
  if (isRecord(presentation) && presentation['rulesheet'] !== undefined) {
    const lines = presentation['rulesheet']
    if (!Array.isArray(lines)) return fail('has a presentation rulesheet that is not a list')
    for (const line of lines) card.push(text(line, 'presentation rulesheet line'))
  }

  return {
    title: text(meta['title'], 'meta.title'),
    subtitle,
    author,
    era,
    difficulty: whole(meta['difficulty'], 'meta.difficulty', 1, 5),
    playersMax: whole(meta['playersMax'], 'meta.playersMax', 1, 4),
    ballsPerGame: whole(meta['ballsPerGame'], 'meta.ballsPerGame', 1, 10),
    ballSeconds,
    rating,
    devices,
    modes,
    mostBalls,
    extraBalls,
    card,
    rulesNotes,
  }
}

/** One mode, as one bullet: its name, its clock, what it asks for and what it pays. */
function modeLine(mode: ModeFacts): string {
  const clock = mode.seconds === undefined ? '' : ` (${mode.seconds} seconds)`
  const parts = [`${mode.title}${clock}`]
  if (mode.shots.length > 0) parts.push(`shots: ${list(mode.shots)}`)
  if (mode.objective !== undefined) parts.push(mode.objective.replace(/\.$/, ''))
  const pays: string[] = []
  if (mode.perShot !== undefined) pays.push(`each shot scores ${points(mode.perShot)}`)
  if (mode.onComplete !== undefined) pays.push(`finishing it scores ${points(mode.onComplete)}`)
  if (pays.length > 0) parts.push(pays.join('; '))
  return `${parts.join(' — ')}.`
}

/** The playfield's devices, in the order `DEVICE_WORDS` lists them, as one clause. */
function playfield(facts: Facts): string {
  const items: string[] = []
  for (const [kind, words] of Object.entries(DEVICE_WORDS)) {
    const n = facts.devices.get(kind) ?? 0
    if (words === null || n === 0) continue
    let item = `${count(n)} ${n === 1 ? words[0] : words[1]}`
    if (kind === 'dropTarget') {
      const banks = facts.devices.get('dropTargetBank') ?? 0
      if (banks > 0) item += ` in ${count(banks)} ${banks === 1 ? 'bank' : 'banks'}`
    }
    items.push(item)
  }
  return list(items)
}

/**
 * The guide's whole copy, for one machine.
 *
 * `free` is whether this machine plays with no account — the bundled flagship — and decides one
 * sentence. It is passed in rather than worked out here because the answer is the bundle's, not
 * the definition's.
 */
export function machineAboutCopy(
  id: string,
  definition: unknown,
  presentation: unknown,
  free: boolean,
): PageCopy {
  const facts = factsOf(id, definition, presentation)
  const name = facts.title
  const modeCount = facts.modes.length

  const title = `${name} rules and modes — ${SITE_NAME}`
  if (title.length > 72) throw new Error(`machine guide: ${id}'s title is too long for a result: ${title}`)

  const modesClause = modeCount === 0 ? '' : `${count(modeCount)} ${modeCount === 1 ? 'mode' : 'modes'}, `
  const description =
    `How to play ${name}: ${modesClause}${facts.ballsPerGame}-ball games for up to ${facts.playersMax} ` +
    `players, its rules and its playfield. Play it in a browser or on a Quest 3.`
  if (description.length < 70 || description.length > 160) {
    throw new Error(`machine guide: ${id}'s description is ${description.length} characters`)
  }

  const game =
    `A game is ${count(facts.ballsPerGame)} balls` +
    (facts.ballSeconds === undefined ? '.' : `, and its author estimates a ball lasts about ${facts.ballSeconds} seconds.`)
  const sections: CopySection[] = [
    {
      heading: 'The machine',
      paragraphs: [
        `${name} is an original pinball machine in the style of a ${facts.era}, for one to ` +
          `${count(facts.playersMax)} ${facts.playersMax === 1 ? 'player' : 'players'}, built by ${facts.author}. ${game} ` +
          `Its author rates it ${facts.difficulty} out of 5 for difficulty, and it is rated for ${facts.rating}.`,
        free
          ? `${name} is free to play in full — every mode and the whole rule set — with no account, no download and no time limit.`
          : `${name} is part of Pro, which unlocks the full roster on every device you sign in on.`,
      ],
      ...(free ? {} : { link: { to: '/pricing', text: 'What Pro costs and what it adds' } }),
    },
    {
      heading: 'On the playfield',
      paragraphs: [
        `The playfield has ${playfield(facts)}. Everything on it — the art, the lettering and every ` +
          'sound — is generated in code when the machine loads, so there is nothing to download.',
      ],
    },
  ]

  if (facts.card.length > 0) {
    sections.push({
      heading: 'The instruction card',
      paragraphs: [`The card on ${name}'s cabinet sums up the rules in ${count(facts.card.length)} lines:`],
      bullets: facts.card,
    })
  }

  if (modeCount > 0) {
    sections.push({
      heading: modeCount === 1 ? 'The mode' : 'The modes',
      paragraphs: [
        modeCount === 1
          ? `${name} has one mode.`
          : `${name} has ${count(modeCount)} modes, listed in the order its rules declare them.`,
      ],
      bullets: facts.modes.map(modeLine),
    })
  }

  if (facts.rulesNotes.length > 0) sections.push({ heading: 'Qualifying and completing modes', bullets: facts.rulesNotes })

  const extras: string[] = []
  if (facts.mostBalls !== undefined) extras.push(`Multiball puts up to ${count(facts.mostBalls)} balls in play at once.`)
  if (facts.extraBalls) extras.push('Extra balls can be earned during a game.')
  if (extras.length > 0) sections.push({ heading: 'Multiball and extra balls', paragraphs: [extras.join(' ')] })

  sections.push({
    heading: 'Playing it',
    paragraphs: [
      `${name} plays in a browser on a computer or a phone, and in a Meta Quest 3 headset. Every ` +
        'game runs the same simulation, stepped a thousand times a second, on every device.',
    ],
    link: { to: '/how-to-play', text: 'The controls on a keyboard, a phone and a headset' },
  })

  return {
    title,
    description,
    h1: name,
    intro: `${facts.subtitle === undefined ? '' : `${facts.subtitle} `}How ${name} plays: its rules, its modes and its playfield.`,
    action: { to: `/play/${id}`, text: `Play ${name}` },
    sections,
  }
}
